import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Send, ArrowRight } from '../icons';
import { generateOutcomeStatement } from '../../lib/progress';
import type { NlpOutcome, GoalType, OutcomeStatus } from '../../lib/progress';

interface OutcomeBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<NlpOutcome>) => void;
  targetUserId: string;
  coachId: string;
  prefillGoal?: { id: string; title: string; description?: string; target_date?: string } | null;
}

const SECTIONS: { key: keyof NlpOutcome; label: string; question: string; textarea: boolean }[] = [
  { key: 'desired_outcome', label: 'Desired outcome', question: 'What specifically does the client want?', textarea: true },
  { key: 'positive_direction', label: 'Positive direction', question: 'What do they want instead of the current problem?', textarea: true },
  { key: 'purpose', label: 'Purpose', question: 'What will achieving this do for them?', textarea: true },
  { key: 'evidence', label: 'Evidence', question: 'How will they know they have achieved it? What will they see, hear and feel?', textarea: true },
  { key: 'measurement_baseline', label: 'Measurement — baseline', question: 'What is the current baseline?', textarea: true },
  { key: 'measurement_target', label: 'Measurement — target', question: 'What is the desired result?', textarea: true },
  { key: 'context', label: 'Context', question: 'Where, when and with whom do they want this?', textarea: true },
  { key: 'personal_control', label: 'Personal control', question: 'What part of achieving it is within the client\u2019s control?', textarea: true },
  { key: 'resources', label: 'Resources', question: 'What skills, beliefs, support or resources are needed?', textarea: true },
  { key: 'ecology', label: 'Ecology', question: 'What might they gain or lose by achieving it? Could it negatively affect another part of life?', textarea: true },
  { key: 'obstacles', label: 'Obstacles', question: 'What could prevent progress?', textarea: true },
  { key: 'milestones_text', label: 'Milestones', question: 'What are the major steps toward the outcome?', textarea: true },
  { key: 'first_action', label: 'First action', question: 'What specific action will the client take next?', textarea: true },
];

export function OutcomeBuilderModal({ isOpen, onClose, onSubmit, targetUserId, coachId, prefillGoal }: OutcomeBuilderModalProps) {
  const [form, setForm] = useState<Record<string, string>>({});
  const [goalType, setGoalType] = useState<GoalType>('outcome');
  const [timeframe, setTimeframe] = useState('');
  const [commitment, setCommitment] = useState(7);
  const [confidence, setConfidence] = useState(7);
  const [outcomeStatement, setOutcomeStatement] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);

  const handlePrefill = () => {
    if (prefillGoal) {
      setForm(prev => ({
        ...prev,
        desired_outcome: prefillGoal.title || '',
        purpose: prefillGoal.description || '',
      }));
      if (prefillGoal.target_date) setTimeframe(prefillGoal.target_date);
    }
  };

  const handleField = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload: Partial<NlpOutcome> = {
        user_id: targetUserId,
        created_by_coach_id: coachId,
        goal_type: goalType,
        status: 'draft',
        ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v || null])),
        timeframe: timeframe || null,
        commitment_level: commitment,
        confidence_level: confidence,
      } as any;
      const { createOutcome } = await import('../../lib/progress');
      const created = await createOutcome(payload as any);
      if (created) {
        const statement = await generateOutcomeStatement(created.id);
        if (statement) setOutcomeStatement(statement);
        await import('../../lib/progress').then(m => m.deleteOutcome(created.id));
      }
    } catch {
      // ignore — coach can write manually
    }
    setGenerating(false);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit({
        user_id: targetUserId,
        created_by_coach_id: coachId,
        goal_type: goalType,
        status: 'proposed',
        ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v || null])),
        timeframe: timeframe || null,
        commitment_level: commitment,
        confidence_level: confidence,
        outcome_statement: outcomeStatement || null,
        upgraded_from_goal_id: prefillGoal?.id || null,
      } as any);
      onClose();
      setForm({});
      setOutcomeStatement('');
      setStep(0);
    } finally {
      setSaving(false);
    }
  };

  const canGenerate = form.desired_outcome && form.evidence && form.measurement_target;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
              <div>
                <h2 className="freud-heading-lg text-chocolate-900">NLP Outcome Builder</h2>
                <p className="text-xs text-chocolate-500">Well-Formed Outcome for your client</p>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-cream-100">
                <X className="w-5 h-5 text-chocolate-500" />
              </button>
            </div>

            {prefillGoal && step === 0 && (
              <div className="px-6 pt-4">
                <div className="bg-sage-50 border border-sage-200 rounded-xl p-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-sage-600 flex-shrink-0" />
                  <p className="text-xs text-sage-700">
                    Upgrading from goal: <strong>{prefillGoal.title}</strong>. Fields pre-filled where possible.
                  </p>
                  <button onClick={handlePrefill} className="ml-auto text-xs font-medium text-sage-700 underline">
                    Pre-fill
                  </button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className="freud-label">Goal type</label>
                <div className="flex gap-2">
                  {(['outcome', 'project', 'habit'] as GoalType[]).map(t => (
                    <button
                      key={t}
                      onClick={() => setGoalType(t)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                        goalType === t ? 'bg-sage-600 text-white' : 'bg-cream-100 text-chocolate-600 hover:bg-cream-200'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {SECTIONS.map((section, i) => (
                <div key={section.key}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-5 h-5 rounded-full bg-cream-200 text-chocolate-500 text-[10px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <label className="text-sm font-medium text-chocolate-800">{section.label}</label>
                  </div>
                  <textarea
                    className="freud-input text-sm"
                    rows={2}
                    placeholder={section.question}
                    value={form[section.key as string] || ''}
                    onChange={(e) => handleField(section.key as string, e.target.value)}
                  />
                </div>
              ))}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="freud-label">Timeframe</label>
                  <input
                    type="date"
                    className="freud-input text-sm"
                    value={timeframe}
                    onChange={(e) => setTimeframe(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="freud-label">Commitment (1-10)</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      className="freud-input text-sm"
                      value={commitment}
                      onChange={(e) => setCommitment(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="freud-label">Confidence (1-10)</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      className="freud-input text-sm"
                      value={confidence}
                      onChange={(e) => setConfidence(Number(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-chocolate-800">Outcome statement</label>
                  <button
                    onClick={handleGenerate}
                    disabled={!canGenerate || generating}
                    className="flex items-center gap-1 text-xs font-medium text-sage-600 hover:text-sage-700 disabled:opacity-40"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {generating ? 'Generating...' : 'Generate with AI'}
                  </button>
                </div>
                <textarea
                  className="freud-input text-sm"
                  rows={3}
                  placeholder="The concise outcome statement shown to the client. Generate with AI or write manually."
                  value={outcomeStatement}
                  onChange={(e) => setOutcomeStatement(e.target.value)}
                />
                {!canGenerate && (
                  <p className="text-[11px] text-chocolate-400 mt-1">
                    Fill in desired outcome, evidence, and measurement target to enable AI generation.
                  </p>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-cream-200 flex items-center justify-between">
              <p className="text-xs text-chocolate-400">
                This will be sent to the client as a proposed outcome for their review.
              </p>
              <button
                onClick={handleSubmit}
                disabled={saving || !form.desired_outcome}
                className="freud-btn-primary flex items-center gap-2 disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
                {saving ? 'Sending...' : 'Send to Client'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
